import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Bitcoin, Copy, ExternalLink, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, explorer, fmtFiat } from "@/lib/api";

const LABEL = { pending: "En attente du paiement", detected: "Paiement détecté dans le mempool", confirmed: "Paiement confirmé", delivered: "Paiement confirmé · codes livrés", overpaid: "Paiement confirmé (sur-payé)", underpaid: "Paiement insuffisant", expired: "Commande expirée" };

function Countdown({ until }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(until) - Date.now())), 500);
    return () => clearInterval(t);
  }, [until]);
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  return <span className="font-mono tabular-nums" data-testid="checkout-countdown-timer">{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

const inputCls = "w-full h-10 rounded-md border border-zinc-700 bg-zinc-950 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50";
const btnCls = "w-full h-10 rounded-md bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-semibold transition-colors disabled:opacity-50 active:scale-[.98]";

function EmailStep({ o, onSent }) {
  const [f, setF] = useState({ name: o.customerName || "", email: o.customerEmail || "", emailConfirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.email.trim().toLowerCase() !== f.emailConfirm.trim().toLowerCase()) return toast.error("Les deux adresses e-mail ne correspondent pas");
    setBusy(true);
    try { await api.post(`/v1/checkout/${o.orderId}/email`, f); toast.success("Code envoyé par e-mail"); onSent(); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="checkout-email-step">
      <div className="flex items-center gap-2 text-sm text-zinc-300 mb-1"><Mail size={15} className="text-amber-500" /> Vérifiez votre e-mail pour recevoir votre commande</div>
      <input className={inputCls} placeholder="Nom / prénom (facultatif)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="checkout-name-input" />
      <input className={inputCls} type="email" required placeholder="Adresse e-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="checkout-email-input" />
      <input className={inputCls} type="email" required placeholder="Confirmez l'adresse e-mail" value={f.emailConfirm} onChange={(e) => setF({ ...f, emailConfirm: e.target.value })} onPaste={(e) => e.preventDefault()} data-testid="checkout-email-confirm-input" />
      <button type="submit" disabled={busy} className={btnCls} data-testid="checkout-send-code-button">{busy ? "Envoi…" : "Recevoir mon code de vérification"}</button>
    </form>
  );
}

function CodeStep({ o, onVerified, onBack, onResent }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(o.attemptsLeft);
  const [expired, setExpired] = useState(false);

  useEffect(() => { setAttemptsLeft(o.attemptsLeft); }, [o.attemptsLeft]);

  useEffect(() => {
    const tick = () => {
      const sentAt = o.codeSentAt ? new Date(o.codeSentAt).getTime() : 0;
      setCooldown(Math.max(0, Math.ceil((sentAt + (o.resendCooldownSeconds || 45) * 1000 - Date.now()) / 1000)));
      setExpired(o.codeExpiresAt ? new Date(o.codeExpiresAt).getTime() < Date.now() : false);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [o.codeSentAt, o.codeExpiresAt, o.resendCooldownSeconds]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/v1/checkout/${o.orderId}/verify`, { code }); toast.success("E-mail vérifié"); onVerified(data); }
    catch (e2) {
      const msg = errMsg(e2); toast.error(msg); setCode("");
      const m = msg.match(/\((\d+) essai/); if (m) setAttemptsLeft(Number(m[1]));
      if (e2?.response?.status === 429 || /invalidé|expiré|Aucun code/i.test(msg)) onResent(false);
    } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/v1/checkout/${o.orderId}/email`, { email: o.customerEmail, emailConfirm: o.customerEmail, name: o.customerName }); toast.success("Nouveau code envoyé"); setCode(""); setAttemptsLeft(5); onResent(true); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="checkout-code-step">
      <div className="text-sm text-zinc-300">Un code à 6 chiffres a été envoyé à <b className="text-zinc-100" data-testid="checkout-sent-to">{o.customerEmail}</b>.</div>
      <input className={`${inputCls} text-center font-mono text-2xl tracking-[.5em] h-14`} inputMode="numeric" maxLength={6} pattern="\d{6}" required placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="checkout-code-input" autoFocus disabled={expired} />
      <div className="flex justify-between text-[11px] text-zinc-500">
        <span data-testid="checkout-attempts-left">{attemptsLeft != null ? `${attemptsLeft} essai(s) restant(s)` : ""}</span>
        <span data-testid="checkout-code-expiry">{expired ? <span className="text-red-400">Code expiré</span> : o.codeExpiresAt ? `Valide jusqu'à ${new Date(o.codeExpiresAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""}</span>
      </div>
      <button type="submit" disabled={busy || code.length !== 6 || expired} className={btnCls} data-testid="checkout-verify-code-button">{busy ? "Vérification…" : "Valider le code"}</button>
      <button type="button" onClick={resend} disabled={busy || cooldown > 0} className="w-full h-9 rounded-md border border-zinc-700 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checkout-resend-code-button">
        {cooldown > 0 ? `Renvoyer le code (${cooldown} s)` : "Renvoyer le code"}
      </button>
      <button type="button" onClick={onBack} className="w-full text-xs text-zinc-500 hover:text-amber-400" data-testid="checkout-change-email-link">Changer d'adresse e-mail</button>
    </form>
  );
}

export default function Checkout() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(null); // 'email' | 'code' | null (auto)

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get(`/v1/checkout/${orderId}`); if (alive) setO(data); }
      catch (e) { if (alive) setErr("Commande introuvable"); }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [orderId]);

  const copy = async (v, label) => { try { await navigator.clipboard.writeText(v); } catch { /* */ } toast.success(label); };

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-400">{err}</div>;
  if (!o) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Chargement…</div>;

  const done = ["confirmed", "overpaid", "delivered"].includes(o.status);
  const isTest = o.mode === "test";
  const currentStep = o.emailVerified ? "pay" : (step || (o.codeSent ? "code" : "email"));

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8">
      <div className="max-w-md w-full mx-auto p-6 sm:p-8 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl fade-up" data-testid="checkout-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-amber-500 grid place-items-center text-zinc-950"><Bitcoin size={18} strokeWidth={2.5} /></span>
            <div>
              <div className="text-sm font-semibold" data-testid="checkout-merchant-name">{o.merchantName}</div>
              <div className="text-[11px] text-zinc-500 font-mono">{o.merchantRef || o.orderId}</div>
            </div>
          </div>
          {isTest && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">TESTNET</span>}
        </div>

        <div className="text-center mb-5">
          {o.productImageUrl && <img src={o.productImageUrl} alt={o.productName || ""} className="h-20 w-20 object-cover rounded-lg mx-auto mb-3 border border-zinc-800" data-testid="checkout-product-image" />}
          {o.productName && <div className="text-sm text-zinc-300 mb-1" data-testid="checkout-product-name">{o.productName}{o.quantity > 1 && ` × ${o.quantity}`}</div>}
          <div className="text-3xl sm:text-4xl font-heading font-bold tracking-tight" data-testid="checkout-btc-amount">{o.amountBtc} <span className="text-amber-500">BTC</span></div>
          <div className="text-sm text-zinc-400 mt-1" data-testid="checkout-fiat-amount">{fmtFiat(o.amountFiat, o.currency)} · 1 BTC = {fmtFiat(o.rate, o.currency)}</div>
        </div>

        {/* étapes */}
        <div className="flex items-center justify-center gap-2 text-[10px] font-mono text-zinc-500 mb-5" data-testid="checkout-steps" data-flow-state={o.flowState}>
          <span className={o.emailVerified ? "text-emerald-400" : "text-amber-400"}>1. E-mail</span><span>›</span>
          <span className={done ? "text-emerald-400" : o.emailVerified ? "text-amber-400" : ""}>2. Paiement</span><span>›</span>
          <span className={o.status === "delivered" ? "text-emerald-400" : done && o.productName ? "text-amber-400" : ""}>3. Livraison</span>
        </div>

        {currentStep === "email" && o.status !== "expired" && <EmailStep o={o} onSent={() => { setStep("code"); api.get(`/v1/checkout/${orderId}`).then((r) => setO(r.data)); }} />}
        {currentStep === "code" && o.status !== "expired" && <CodeStep o={o} onVerified={(data) => { setO(data); setStep(null); }} onBack={() => setStep("email")} onResent={() => api.get(`/v1/checkout/${orderId}`).then((r) => { setO(r.data); if (!r.data.codeSent) setStep("email"); })} />}

        {currentStep === "pay" && (<>
          <div className="flex items-center justify-center mb-5">
            <span data-testid="checkout-status-badge" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono border ${done ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : o.status === "expired" || o.status === "underpaid" ? "bg-red-500/15 text-red-400 border-red-500/30" : o.status === "detected" ? "bg-blue-500/15 text-blue-400 border-blue-500/30" : "bg-zinc-800 text-zinc-300 border-zinc-700"}`}>
              {!done && o.status !== "expired" && <span className="h-2 w-2 rounded-full bg-current pulse-ring" />}
              {LABEL[o.status] || o.status}
            </span>
          </div>

          {done ? (
            <div className="py-4 flex flex-col items-center" data-testid="checkout-success">
              <svg width="72" height="72" viewBox="0 0 88 88" className="mb-3"><circle cx="44" cy="44" r="40" className="stroke-emerald-500/40" strokeWidth="3" fill="none" /><path d="M26 45 L39 58 L63 32" className="check-path stroke-emerald-400" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <div className="text-lg font-semibold">Merci, paiement reçu !</div>
              <div className="text-xs text-zinc-500 mt-1">{o.confirmations} confirmation(s) · {o.receivedSats.toLocaleString()} sats</div>
              {o.status === "delivered" && o.codes?.length > 0 && (
                <div className="w-full mt-5" data-testid="checkout-codes">
                  <div className="eyebrow mb-2 flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-400" /> Vos codes {o.productName && `— ${o.productName}`}</div>
                  <div className="space-y-2">
                    {o.codes.map((c, i) => (
                      <div key={i} className="mono-box flex items-center justify-between gap-2 text-emerald-300"><span data-testid="checkout-code-value">{c}</span><button onClick={() => copy(c, "Code copié")} className="text-zinc-400 hover:text-amber-400" data-testid="copy-code-button"><Copy size={14} /></button></div>
                    ))}
                  </div>
                  <div className="text-xs text-zinc-500 mt-2" data-testid="checkout-delivery-email-note">{o.deliveryEmailSent ? `Une copie a été envoyée à ${o.customerEmail}.` : "Conservez ces codes : l'envoi par e-mail n'a pas pu aboutir."}</div>
                </div>
              )}
              {o.status !== "delivered" && o.productName && <div className="text-xs text-amber-400 mt-4" data-testid="checkout-delivery-pending">{o.deliveryError ? "Livraison en cours de traitement par le marchand." : "Préparation de vos codes…"}</div>}
              {o.returnUrl && <a href={o.returnUrl} className="mt-6 px-5 py-2 rounded-md bg-amber-500 text-zinc-950 text-sm font-semibold hover:bg-amber-400 transition-colors" data-testid="checkout-return-link">Retour à la boutique</a>}
            </div>
          ) : o.status === "expired" ? (
            <div className="py-8 text-center text-sm text-zinc-400" data-testid="checkout-expired">Le délai de paiement est dépassé. Retournez sur la boutique pour relancer une commande.{o.returnUrl && <div><a href={o.returnUrl} className="text-amber-400 hover:underline mt-3 inline-block">Retour</a></div>}</div>
          ) : (
            <>
              <div className="bg-white p-3 rounded-xl w-fit mx-auto mb-5" data-testid="checkout-qr-code">
                <QRCodeSVG value={o.bip21} size={196} level="M" />
              </div>
              <div className="space-y-2 mb-5">
                <div className="eyebrow">Adresse de paiement (BIP84)</div>
                <div className="mono-box flex items-start justify-between gap-2">
                  <span data-testid="checkout-btc-address">{o.address}</span>
                  <button onClick={() => copy(o.address, "Adresse BTC copiée")} className="shrink-0 text-zinc-400 hover:text-amber-400 active:scale-95 transition" data-testid="copy-address-button"><Copy size={15} /></button>
                </div>
                <div className="flex justify-between text-xs text-zinc-500">
                  <button onClick={() => copy(o.amountBtc, "Montant copié")} className="hover:text-amber-400" data-testid="copy-amount-button">Copier le montant</button>
                  <a href={o.bip21} className="hover:text-amber-400" data-testid="open-wallet-link">Ouvrir dans le wallet</a>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm border-t border-zinc-800 pt-4">
                <span className="text-zinc-400">Expire dans</span><Countdown until={o.expiresAt} />
              </div>
              {o.status === "detected" && <div className="text-xs text-zinc-400 mt-3 text-center" data-testid="checkout-confirmations">{o.confirmations}/{o.confirmationsRequired} confirmation(s) · {o.receivedSats.toLocaleString()} sats reçus</div>}
            </>
          )}
        </>)}

        {o.status === "expired" && !o.emailVerified && <div className="py-6 text-center text-sm text-zinc-400" data-testid="checkout-expired">Le délai de paiement est dépassé.</div>}

        {o.txids?.length > 0 && (
          <div className="mt-4 text-xs space-y-1">
            {o.txids.slice(0, 3).map((t) => <a key={t} href={explorer(o.network, t)} target="_blank" rel="noreferrer" className="flex items-center gap-1 font-mono text-zinc-500 hover:text-amber-400 break-all" data-testid="checkout-txid-link"><ExternalLink size={11} /> {t}</a>)}
          </div>
        )}
        <div className="mt-6 flex items-center justify-between text-[10px] text-zinc-600">
          {o.emailVerified ? <span className="flex items-center gap-1 text-emerald-500/80" data-testid="checkout-email-verified-badge"><ShieldCheck size={11} /> {o.customerEmail}</span> : <span />}
          <span>Non-custodial · fonds envoyés directement au marchand</span>
        </div>
      </div>
    </div>
  );
}
